import { RpcError } from "grpc-web";
import {
  AcceptGuestTermsRequest,
  AcceptGuestTermsResponse,
  CheckGuestSessionRequest,
  CheckGuestSessionResponse,
  CreateGuestSessionRequest,
  CreateGuestSessionResponse,
  ClaimGuestSessionRequest,
  ClaimGuestSessionResponse,
} from "../proto/api_pb";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import {
  getStoredGuestSession,
  storeGuestSession,
  clearStoredGuestSession,
  markGuestSession,
} from "./guestStorage";
import type { GuestSession } from "./guestStorage";

// Guest-session storage is backend-scoped and lives in ./guestStorage (a pure,
// proto-free module). Re-exported here so existing importers of
// guestSessionService keep working unchanged.
export { getStoredGuestSession, clearStoredGuestSession, markGuestSession };
export {
  clearGuestSession,
  migrateLegacyGuestStorage,
  isGuestAuthError,
  peekGuestExpiryMs,
  backendTag,
  incrementGuestUsage,
  GUEST_USAGE_EVENT,
} from "./guestStorage";
export type { GuestSession };
export type { LegacyMigrationResult } from "./guestStorage";

/**
 * Mint a fresh guest session against the API and persist the token to
 * localStorage. The caller is expected to navigate the user into the app
 * shell on success. Terms acceptance is collected later at first upload via
 * acceptGuestTerms().
 */
export const createGuestSession = async (): Promise<GuestSession> => {
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new CreateGuestSessionRequest();
  // Field is kept for backward compatibility; the server ignores it.
  req.setAcceptedTerms(false);

  const invoke = (metadata: Record<string, string>) =>
    new Promise<CreateGuestSessionResponse>((resolve, reject) => {
      client.createGuestSession(req, metadata, (err: RpcError | null, resp) => {
        if (err) reject(err);
        else resolve(resp);
      });
    });

  // Token-first resume: when we still hold a guest token, present it so the
  // server resumes THAT exact session (see CreateGuestSession's token-first
  // path). This is what keeps a returning guest's uploaded work across network
  // changes / shared IPs, where the server's IP-hash fallback can't attribute a
  // session to us. A stale/invalid token must never block minting a fresh
  // session, so on an auth rejection we retry once anonymously.
  const stored = getStoredGuestSession();
  let response: CreateGuestSessionResponse;
  try {
    response = await invoke(
      stored?.token ? { Authorization: stored.token } : {}
    );
  } catch (err) {
    const code = (err as RpcError)?.code;
    // grpc-web status codes: 16 = UNAUTHENTICATED, 7 = PERMISSION_DENIED.
    if (stored?.token && (code === 16 || code === 7)) {
      response = await invoke({});
    } else {
      throw err;
    }
  }

  // getStorageUsedBytes() is added to the generated bindings by the proto regen
  // (CI). Read it defensively so the app still type-checks against the pre-regen
  // bindings; falls back to 0 (correct for a fresh session) until the
  // regenerated proto lands.
  const respWithUsage = response as CreateGuestSessionResponse & {
    getStorageUsedBytes?: () => number;
  };
  const usedBytes =
    typeof respWithUsage.getStorageUsedBytes === "function"
      ? Number(respWithUsage.getStorageUsedBytes()) || 0
      : 0;

  const session: GuestSession = {
    token: response.getToken(),
    sessionId: response.getSessionId(),
    expiresAtMs: response.getExpiresAtMs(),
    quotaBytes: response.getQuotaBytes(),
    usedBytes,
  };
  storeGuestSession(session);
  markGuestSession();
  return session;
};

/**
 * Send the stored guest token alongside an Auth0 access token so the server
 * can merge the guest's tracks/projects into the just-signed-up account.
 * Returns null when there is nothing to claim (incognito browser, expired
 * session, etc.) so the caller can branch on that without throwing.
 */
export const claimGuestSession = async (
  auth0Token: string
): Promise<ClaimGuestSessionResponse | null> => {
  const stored = getStoredGuestSession();
  if (!stored) return null;
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new ClaimGuestSessionRequest();
  req.setGuestToken(stored.token);
  const metadata = { Authorization: auth0Token };
  const resp = await new Promise<ClaimGuestSessionResponse>((resolve, reject) => {
    client.claimGuestSession(req, metadata, (err: RpcError | null, r) => {
      if (err) reject(err);
      else resolve(r);
    });
  });
  // Clear ONLY on success — if the API call threw, the token is preserved
  // so GuestClaimGate can retry on the next mount.
  clearStoredGuestSession();
  return resp;
};

/**
 * Stamp the guest user document with terms-accepted-at. Called by the
 * GuestTermsModal when the guest checks the consent box and clicks Continue.
 * Requires the caller to attach the guest JWT as the Authorization header
 * (the auth interceptor allow-lists this RPC for guest tokens).
 */
export const acceptGuestTerms = async (): Promise<number> => {
  const stored = getStoredGuestSession();
  if (!stored) {
    throw new Error("No guest session in localStorage");
  }
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const req = new AcceptGuestTermsRequest();
  const metadata = { Authorization: stored.token };
  const response = await new Promise<AcceptGuestTermsResponse>(
    (resolve, reject) => {
      client.acceptGuestTerms(req, metadata, (err: RpcError | null, resp) => {
        if (err) reject(err);
        else resolve(resp);
      });
    }
  );
  return response.getAcceptedAtMs();
};

/**
 * Probe whether the current IP has an active guest session on the server.
 * No side effects — no token is minted, no TTL is slid. Safe to call on
 * every Welcome page mount. Returns false on any network or server error so
 * the probe never blocks the page.
 */
export const checkGuestSession = async (): Promise<boolean> => {
  const client = new OmmuseClient(process.env.REACT_APP_API_URL!);
  const response = await new Promise<CheckGuestSessionResponse>(
    (resolve, reject) => {
      client.checkGuestSession(
        new CheckGuestSessionRequest(),
        {},
        (err: RpcError | null, resp) => {
          if (err) reject(err);
          else resolve(resp);
        }
      );
    }
  );
  return response.getHasActiveSession();
};
